import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Descriptions, Input, Select, Space, Spin, Typography } from 'antd'
import * as api from '../lib/api'
import EmployeeForm from './EmployeeForm'
import { StatusBadge } from './HRDashboard'
import ReturnSidePanel from './ReturnSidePanel'
import { businessUnitThemeVars, getBusinessUnitTheme } from './businessUnitTheme'

const { Title, Text } = Typography
const { TextArea } = Input
const DEFAULT_TEMPLATE_CODE = 'hk_tti'

function ReviewerWorkflowPreview({ appraisal }) {
  const [comments, setComments] = useState({})
  const [returnTarget, setReturnTarget] = useState(null)
  const [returnComments, setReturnComments] = useState('')
  const approvers = [1, 2, 3, 4, 5].map(level => ({
    level,
    name: appraisal[`approver_${level}_name`] || `Approver ${level}`
  }))
  const previousApproverName = approvers[3].name
  const returnRecipient = returnTarget === 'employee' ? 'Employee' : previousApproverName

  useEffect(() => {
    setComments({})
    setReturnTarget(null)
    setReturnComments('')
  }, [appraisal.session_template])

  const openReturnPanel = target => {
    if (returnTarget !== target) setReturnComments('')
    setReturnTarget(target)
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div className='tti-appraisal-form' style={{ flex: 1, minWidth: 0, maxWidth: 1600, margin: '0 auto', padding: '0 20px 104px', ...businessUnitThemeVars(getBusinessUnitTheme(appraisal)) }}>
        {approvers.map(({ level, name }) => (
          <Card
            key={level}
            className='tti-appraisal-section-card'
            title={<Text style={{ color: '#FFF' }}>Comments &amp; Sign-off - {name}</Text>}
            style={{ marginBottom: 24 }}
          >
            <TextArea
              rows={4}
              value={comments[level] || ''}
              onChange={event => setComments(current => ({ ...current, [level]: event.target.value }))}
              placeholder={`Add comments by ${name}`}
            />
          </Card>
        ))}

        <Card className='tti-appraisal-section-card' title={<Text style={{ color: '#FFF' }}>Reviewed / Approvals</Text>} style={{ marginBottom: 24 }}>
          <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            <Descriptions column={1} size='small'>
              <Descriptions.Item label='Appraisal Status'>
                <StatusBadge status={appraisal.status} label={appraisal.status_label} />
              </Descriptions.Item>
            </Descriptions>
            <Card size='small' type='inner' title='Employee'>
              <Descriptions column={1} size='small'>
                <Descriptions.Item label='Submitted By'>Employee</Descriptions.Item>
                <Descriptions.Item label='Status'>Pending</Descriptions.Item>
                <Descriptions.Item label='Date'>Not recorded</Descriptions.Item>
              </Descriptions>
            </Card>
            {approvers.map(({ level, name }) => (
              <Card key={level} size='small' type='inner' title={<Text strong>{`Approver ${level}`}</Text>}>
                <Descriptions column={1} size='small'>
                  <Descriptions.Item label='Approver'>{name}</Descriptions.Item>
                  <Descriptions.Item label='Status'>Pending</Descriptions.Item>
                  <Descriptions.Item label='Date'>Not recorded</Descriptions.Item>
                  {comments[level]?.trim() && (
                    <Descriptions.Item label='Comments'>{comments[level]}</Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            ))}
          </Space>
        </Card>

        <Alert
          type='info'
          showIcon
          style={{ marginBottom: 24 }}
          message='Dynamic approver name'
          description={`In the live workflow, “previous approver” is replaced with the assigned approver's actual name, for example “${previousApproverName}”.`}
        />

        <div className='tti-form-action-bar translation-review-action-bar'>
          <div className='tti-form-action-bar__inner'>
            <Space size='middle' wrap>
              <Button size='large' disabled className='translation-review-cancel'>Cancel</Button>
              <Button size='large' disabled className='tti-btn-primary-grey'>Save Draft</Button>
              <Button danger size='large' className='translation-review-danger' onClick={() => openReturnPanel('previous')}>
                Reject and return to previous approver
              </Button>
              <Button danger size='large' className='translation-review-danger' onClick={() => openReturnPanel('employee')}>
                Return to Employee
              </Button>
              <Button type='primary' size='large' disabled>Approve &amp; Complete</Button>
            </Space>
          </div>
        </div>
      </div>
      <ReturnSidePanel
        open={Boolean(returnTarget)}
        title={`Return to ${returnRecipient}`}
        description={`Preview the note that would be sent to ${returnRecipient}. Nothing will be saved or sent.`}
        value={returnComments}
        onChange={setReturnComments}
        onCancel={() => setReturnTarget(null)}
        onSubmit={() => setReturnTarget(null)}
        submitLabel={`Return to ${returnRecipient}`}
        fixedRight
      />
    </div>
  )
}

export default function TranslationReviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const requestedTemplate = searchParams.get('template') || ''

  useEffect(() => {
    api.getTranslationReviewTemplates()
      .then(setTemplates)
      .catch(requestError => setError(requestError.message || 'Unable to load appraisal templates.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!templates.length) return
    const defaultTemplate = templates.find(template => template.code === DEFAULT_TEMPLATE_CODE) || templates[0]
    const selectedCode = templates.some(template => template.code === requestedTemplate)
      ? requestedTemplate
      : defaultTemplate.code
    if (requestedTemplate === selectedCode && !searchParams.has('locale')) return
    const next = new URLSearchParams(searchParams)
    next.set('template', selectedCode)
    next.delete('locale')
    setSearchParams(next, { replace: true })
  }, [requestedTemplate, searchParams, setSearchParams, templates])

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size='large' /></div>
  if (error) return <Alert type='error' showIcon message='Translation review unavailable' description={error} />
  if (!templates.length) return <Alert type='info' showIcon message='No review templates are available.' />

  const template = templates.find(item => item.code === requestedTemplate) ||
    templates.find(item => item.code === DEFAULT_TEMPLATE_CODE) || templates[0]
  const updateTemplate = value => {
    const next = new URLSearchParams(searchParams)
    next.set('template', value)
    next.delete('locale')
    setSearchParams(next)
  }
  const appraisal = {
    id: null,
    status: 'completed',
    status_label: 'Translation review preview',
    session_name: template.name,
    session_template: template.code,
    session_template_name: template.name,
    session_organization_structure: 'business_unit_department',
    session_business_unit_logo_url: template.logo_url,
    session_business_unit_primary_color: template.primary_color,
    session_business_unit_secondary_color: template.secondary_color,
    employee_first_name: 'Employee',
    employee_last_name: '',
    employee_email: '',
    employee_company_id: '',
    employee_position: '',
    department_name: '',
    department_code: '',
    business_unit_name: template.organization_name,
    approver_1: true,
    approver_1_name: 'Approver 1',
    approver_2: true,
    approver_2_name: 'Approver 2',
    approver_3: true,
    approver_3_name: 'Approver 3',
    approver_4: true,
    approver_4_name: 'Approver 4',
    approver_5: true,
    approver_5_name: 'Approver 5',
    form_data: {},
    can_export_pdf: false,
    return_history: [],
    functional_reviews: []
  }

  return (
    <div>
      <div style={{ maxWidth: 1600, margin: '0 auto', padding: '20px 20px 0' }}>
        <Title level={2} style={{ marginBottom: 4 }}>Appraisal Form Translation Review</Title>
        <Text type='secondary'>Select Appraisal template</Text>
        <Select
          aria-label='Appraisal template'
          showSearch
          value={template.code}
          style={{ minWidth: 320, marginTop: 20 }}
          optionFilterProp='label'
          options={templates.map(item => ({ value: item.code, label: item.name }))}
          onChange={updateTemplate}
        />
      </div>
      <EmployeeForm appraisal={appraisal} translationReview />
      <ReviewerWorkflowPreview appraisal={appraisal} />
    </div>
  )
}
